import { getSession } from "@/lib/auth/session";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { KpiCard } from "@/components/ui/kpi-card";
import {
  IconActivity,
  IconBuilding,
  IconFileText,
  IconUsers,
} from "@/components/icons";
import {
  getDashboardStats,
  topActiveTenants,
} from "@/lib/db/queries/dashboard";
import { logActivity } from "@/lib/db/audit";

export const dynamic = "force-dynamic";

const numFmt = new Intl.NumberFormat("fr-FR");

export default async function DashboardPage() {
  const session = await getSession();
  const user = session!.user;
  const firstName = user.name.split(/\s+/)[0] || user.email;
  const today = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  let stats: Awaited<ReturnType<typeof getDashboardStats>> | null = null;
  let topTenants: Awaited<ReturnType<typeof topActiveTenants>> = [];
  let loadError: string | null = null;
  try {
    [stats, topTenants] = await Promise.all([
      getDashboardStats(),
      topActiveTenants(),
    ]);
    void logActivity({
      type: "superadmin.dashboard_view",
      title: "Vue tableau de bord cross-tenant",
      user_id: user.id,
    });
  } catch (err) {
    loadError = err instanceof Error ? err.message : "Erreur inconnue";
  }

  return (
    <div className="px-6 py-8 lg:px-10">
      <header className="mb-8 flex flex-col gap-1 mf-fade-in">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
          {today}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Bonjour {firstName} 👋
        </h1>
        <p className="text-sm text-muted-foreground">
          Vue d&apos;ensemble du SaaS missioflow.
        </p>
      </header>

      {loadError && (
        <div className="mb-6 rounded-md border border-red-200 bg-danger-subtle px-4 py-3 text-sm text-danger">
          Impossible de charger les KPIs : {loadError}
        </div>
      )}

      {!loadError && stats && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label="Tenants"
              value={numFmt.format(stats.tenants_total)}
              hint={`${stats.tenants_active} actif(s) · ${stats.tenants_trial} trial · ${stats.tenants_suspended} suspendu(s)`}
              icon={<IconBuilding size={14} />}
              tone="accent"
            />
            <KpiCard
              label="Techniciens"
              value={numFmt.format(stats.techniciens_total)}
              hint={`${stats.techniciens_actifs} actifs · ${stats.superadmins} super-admin(s)`}
              icon={<IconUsers size={14} />}
              tone="neutral"
            />
            <KpiCard
              label="Interventions (30 j)"
              value={numFmt.format(stats.interventions_30j)}
              hint={`${numFmt.format(stats.interventions_total)} au total`}
              icon={<IconActivity size={14} />}
              tone="success"
            />
            <KpiCard
              label="API (24 h)"
              value={numFmt.format(stats.api_calls_24h)}
              hint={
                stats.api_errors_24h > 0
                  ? `${stats.api_errors_24h} erreurs`
                  : "aucune erreur"
              }
              icon={<IconFileText size={14} />}
              tone={stats.api_errors_24h > 0 ? "warning" : "neutral"}
            />
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2 mf-fade-in">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle>Tenants les plus actifs (30 j)</CardTitle>
                    <CardDescription>
                      Par nombre d&apos;interventions créées sur la période.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {topTenants.length === 0 ? (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                    Aucun tenant à afficher.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {topTenants.map((t, i) => (
                      <li
                        key={t.id}
                        className="flex items-center gap-3 py-2.5 text-sm"
                      >
                        <span className="w-5 text-right text-xs font-medium text-muted-foreground tabular-nums">
                          {i + 1}.
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{t.name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {t.slug}
                          </div>
                        </div>
                        <span className="tabular-nums text-foreground">
                          {numFmt.format(t.intervention_count_30j)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          inter.
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card className="mf-fade-in">
              <CardHeader>
                <CardTitle>Volume métier</CardTitle>
                <CardDescription>30 derniers jours.</CardDescription>
              </CardHeader>
              <CardBody>
                <dl className="space-y-3 text-sm">
                  <Row label="Sites clients" value={stats.sites} />
                  <Row label="Machines" value={stats.machines} />
                  <Row label="Rapports (30 j)" value={stats.rapports_30j} />
                  <Row label="Factures (30 j)" value={stats.factures_30j} />
                </dl>
              </CardBody>
            </Card>
          </div>

          <div className="mt-8">
            <Card className="mf-fade-in">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle>Statut système</CardTitle>
                  <Badge tone={stats.api_errors_24h > 10 ? "warning" : "success"}>
                    {stats.api_errors_24h > 10 ? "À surveiller" : "Stable"}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-muted-foreground">
                  Bascule du panel en Option B (lecture MySQL directe) actée le
                  2026-05-09. Le panel est désormais autonome sur tous les
                  listings ; les mutations sensibles passent toujours par
                  missioflow-app.
                </p>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-2 last:border-0 last:pb-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums">{numFmt.format(value)}</dd>
    </div>
  );
}
